/**
 * Naija Property - Component Library
 *
 * Complete source of truth for all reusable UI components
 * Organized by atomic design: Atoms → Molecules → Organisms
 *
 * @usage
 * import { Button, Input, Card, Section } from './components/ComponentLibrary';
 */

import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { SITE_URL } from '../seoUtils';
import { EyeIcon, EyeOffIcon, ChevronLeftIcon } from './IconSet';
import { useSmartBack } from '../hooks/useSmartBack';

// ============================================================================
// ATOMS - Basic Building Blocks
// ============================================================================

/**
 * Button - Reusable button component with variants
 * @param {Object} props
 * @param {string} [props.variant="primary"] - "primary", "secondary", "text"
 * @param {boolean} [props.disabled=false] - Disable the button
 * @param {string} [props.type="button"] - "button", "submit", "reset"
 * @param {React.ReactNode} [props.icon] - Optional icon to render before text
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} props.children - Button text or content
 * @param {function} [props.onClick] - Click handler
 */
export function Button({
  variant = 'primary',
  size,
  disabled = false,
  type = 'button',
  icon,
  className = '',
  children,
  onClick,
  ...rest
}) {
  const baseClass = 'ui-button';
  const variantClass = variant === 'primary' ? '' : `${baseClass}--${variant}`;
  const sizeClass = size ? `${baseClass}--${size}` : '';
  const disabledClass = disabled ? `${baseClass}--disabled` : '';
  const finalClass = [baseClass, variantClass, sizeClass, disabledClass, className]
    .filter(Boolean)
    .join(' ');

  return (
    <button type={type} className={finalClass} disabled={disabled} onClick={onClick} {...rest}>
      {icon && <span className="ui-button__icon">{icon}</span>}
      <span className="ui-button__text">{children}</span>
    </button>
  );
}

/**
 * Input - Reusable input component
 * @param {Object} props
 * @param {string} [props.type="text"] - Input type (text, email, number, etc.)
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.value] - Input value
 * @param {function} [props.onChange] - Change handler
 * @param {boolean} [props.required=false] - Mark as required
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ReactNode} [props.icon] - Optional icon element
 * @param {string} [props.id] - Input ID for label association
 */
export function Input({
  type = 'text',
  placeholder,
  value,
  onChange,
  required = false,
  className = '',
  icon,
  id,
  ...rest
}) {
  const wrapperClass = icon ? 'ui-input-wrapper' : '';

  return (
    <div className={wrapperClass}>
      {icon && <span className="ui-input-icon">{icon}</span>}
      <input
        type={type}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
        required={required}
        className={`ui-input ${className}`.trim()}
        id={id}
        {...rest}
      />
    </div>
  );
}

/**
 * PasswordInput - Password field with a show/hide toggle
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes for the input (merged with the page's own input class)
 * @param {string} [props.id] - Input ID for label association
 */
export function PasswordInput({ className = '', id, ...rest }) {
  const [visible, setVisible] = useState(false);

  return (
    <div className="ui-password-wrapper">
      <input
        type={visible ? 'text' : 'password'}
        id={id}
        className={`ui-password-input ${className}`.trim()}
        {...rest}
      />
      <button
        type="button"
        className="ui-password-toggle"
        onClick={() => setVisible((v) => !v)}
        aria-label={visible ? 'Hide password' : 'Show password'}
      >
        {visible ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
      </button>
    </div>
  );
}

/**
 * TextArea - Reusable multiline text input
 * @param {Object} props
 * @param {string} [props.placeholder] - Placeholder text
 * @param {string} [props.value] - Current text value
 * @param {function} [props.onChange] - Change handler
 * @param {boolean} [props.required=false] - Mark as required
 * @param {number} [props.rows=4] - Visible rows
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.id] - Input ID for label association
 */
export function TextArea({
  placeholder,
  value,
  onChange,
  required = false,
  rows = 4,
  className = '',
  id,
  ...rest
}) {
  return (
    <textarea
      placeholder={placeholder}
      value={value}
      onChange={onChange}
      required={required}
      rows={rows}
      className={`ui-textarea ${className}`.trim()}
      id={id}
      {...rest}
    />
  );
}

/**
 * Select - Reusable select/dropdown component
 * @param {Object} props
 * @param {string} props.value - Current value
 * @param {function} props.onChange - Change handler
 * @param {Object[]} [props.options=[]] - Array of {value, label} objects
 * @param {string} [props.placeholder] - Default option label
 * @param {boolean} [props.disabled=false] - Disable the select
 * @param {string} [props.className] - Additional CSS classes
 * @param {string} [props.id] - Select ID for label association
 */
export function Select({
  value,
  onChange,
  options = [],
  placeholder,
  disabled = false,
  className = '',
  id,
  ...rest
}) {
  return (
    <select
      value={value}
      onChange={onChange}
      disabled={disabled}
      className={`ui-select ${className}`.trim()}
      id={id}
      {...rest}
    >
      {placeholder && <option value="">{placeholder}</option>}
      {options.map((opt) => (
        <option key={opt.value} value={opt.value}>
          {opt.label}
        </option>
      ))}
    </select>
  );
}

/**
 * Badge - Small label or status indicator
 * @param {Object} props
 * @param {React.ReactNode} props.children - Badge content
 * @param {string} [props.variant="info"] - "info", "success", "warning", "error"
 * @param {React.ReactNode} [props.icon] - Optional icon
 * @param {string} [props.className] - Additional CSS classes
 */
export function Badge({ children, variant = 'info', icon, className = '', ...rest }) {
  const baseClass = 'ui-badge';
  const variantClass = `${baseClass}--${variant}`;
  const finalClass = [baseClass, variantClass, className].filter(Boolean).join(' ');

  return (
    <span className={finalClass} {...rest}>
      {icon && <span className="ui-badge__icon">{icon}</span>}
      {children}
    </span>
  );
}

/**
 * Tag - Small chip-style label
 * @param {Object} props
 * @param {React.ReactNode} props.children - Tag content
 * @param {boolean} [props.filled=false] - Whether to use filled style
 * @param {string} [props.className] - Additional CSS classes
 */
export function Tag({ children, filled = false, className = '', ...rest }) {
  const baseClass = 'ui-tag';
  const filledClass = filled ? `${baseClass}--filled` : '';
  const finalClass = [baseClass, filledClass, className].filter(Boolean).join(' ');

  return (
    <span className={finalClass} {...rest}>
      {children}
    </span>
  );
}

/**
 * Tooltip - Lightweight hover/focus helper text wrapper
 * @param {Object} props
 * @param {React.ReactNode} props.children - Trigger element
 * @param {React.ReactNode} props.content - Tooltip content
 * @param {string} [props.position="top"] - "top" | "bottom"
 * @param {string} [props.className] - Additional CSS classes
 */
export function Tooltip({ children, content, position = 'top', className = '' }) {
  if (!content) {
    return children;
  }

  return (
    <span
      className={['ui-tooltip', `ui-tooltip--${position}`, className].filter(Boolean).join(' ')}
    >
      <span className="ui-tooltip__trigger">{children}</span>
      <span className="ui-tooltip__bubble" role="tooltip">
        {content}
      </span>
    </span>
  );
}

// ============================================================================
// MOLECULES - Combinations of Atoms
// ============================================================================

/**
 * FormField - Label + Input combination with error handling
 * @param {Object} props
 * @param {string} props.label - Field label
 * @param {string} [props.id] - Input ID
 * @param {React.ReactNode} props.children - Input element(s)
 * @param {string} [props.error] - Error message
 * @param {string} [props.className] - Additional CSS classes
 */
export function FormField({ label, id, children, error, className = '', ...rest }) {
  const finalClass = ['ui-form-field', error && 'ui-form-field--error', className]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={finalClass} {...rest}>
      {label && <label htmlFor={id}>{label}</label>}
      {children}
      {error && <span className="ui-form-field__error">{error}</span>}
    </div>
  );
}

/**
 * Card - Container with optional header, body, and footer
 * @param {Object} props
 * @param {React.ReactNode} props.children - Card content
 * @param {string} [props.variant="default"] - "default", "elevated", "outlined"
 * @param {React.ReactNode} [props.header] - Optional header content
 * @param {React.ReactNode} [props.footer] - Optional footer content
 * @param {string} [props.className] - Additional CSS classes
 */
export function Card({ children, variant = 'default', header, footer, className = '', ...rest }) {
  const baseClass = 'ui-card';
  const variantClass = variant !== 'default' ? `${baseClass}--${variant}` : '';
  const finalClass = [baseClass, variantClass, className].filter(Boolean).join(' ');

  return (
    <div className={finalClass} {...rest}>
      {header && <div className="ui-card__header">{header}</div>}
      <div className="ui-card__body">{children}</div>
      {footer && <div className="ui-card__footer">{footer}</div>}
    </div>
  );
}

/**
 * MetaLine - Icon + text label (e.g., location, property type)
 * @param {Object} props
 * @param {React.ReactNode} props.icon - Icon element
 * @param {React.ReactNode} props.children - Text content
 * @param {string} [props.className] - Additional CSS classes
 */
export function MetaLine({ icon, children, className = '', ...rest }) {
  const finalClass = ['ui-meta-line', className].filter(Boolean).join(' ');

  return (
    <div className={finalClass} {...rest}>
      {icon && <span className="ui-meta-line__icon">{icon}</span>}
      <span className="ui-meta-line__text">{children}</span>
    </div>
  );
}

/**
 * RadioCard - Card-style radio button for selections
 * @param {Object} props
 * @param {string} props.label - Main label
 * @param {string} [props.description] - Sub-text
 * @param {string} props.value - Input value
 * @param {boolean} props.checked - Is checked
 * @param {function} props.onChange - Change handler
 * @param {React.ReactNode} [props.icon] - Optional icon
 * @param {string} [props.className] - Additional CSS classes
 */
export function RadioCard({
  label,
  description,
  value,
  checked,
  onChange,
  icon,
  className = '',
  ...rest
}) {
  const finalClass = ['ui-radio-card', checked && 'ui-radio-card--checked', className]
    .filter(Boolean)
    .join(' ');

  return (
    <label className={finalClass} {...rest}>
      <input
        type="radio"
        value={value}
        checked={checked}
        onChange={onChange}
        className="ui-radio-card__input"
      />
      <span className="ui-radio-card__content">
        {icon && <span className="ui-radio-card__icon">{icon}</span>}
        <span className="ui-radio-card__labels">
          <strong className="ui-radio-card__label">{label}</strong>
          {description && <small className="ui-radio-card__description">{description}</small>}
        </span>
      </span>
    </label>
  );
}

/**
 * Alert - Status message with icon and proper ARIA attributes
 * @param {Object} props
 * @param {React.ReactNode} props.children - Alert message
 * @param {string} [props.type="info"] - "info", "success", "warning", "error"
 * @param {React.ReactNode} [props.icon] - Optional icon
 * @param {boolean} [props.srOnly] - Include screen-reader-only prefix
 * @param {string} [props.className] - Additional CSS classes
 */
export function Alert({ children, type = 'info', icon, srOnly = false, className = '', ...rest }) {
  const baseClass = 'ui-alert';
  const typeClass = `${baseClass}--${type}`;
  const finalClass = [baseClass, typeClass, className].filter(Boolean).join(' ');

  const role = type === 'error' ? 'alert' : 'status';
  const ariaLive = type === 'error' ? 'assertive' : 'polite';

  const typeLabels = {
    info: 'Info: ',
    success: 'Success: ',
    warning: 'Warning: ',
    error: 'Error: ',
  };

  return (
    <div className={finalClass} role={role} aria-live={ariaLive} {...rest}>
      {icon && <span className="ui-alert__icon">{icon}</span>}
      {srOnly && <span className="sr-only">{typeLabels[type]}</span>}
      <span className="ui-alert__message">{children}</span>
    </div>
  );
}

/**
 * Breadcrumbs - Hierarchical navigation trail
 *
 * On mobile, renders as a `← Back` + page title bar instead of the full
 * trail - breadcrumbs read fine with room to spread out, but cramp badly at
 * phone widths. Back goes to real browser history when there is any
 * (see useSmartBack), falling back to the second-to-last item's href for a
 * cold load or a shared link.
 *
 * @param {Object} props
 * @param {Array} [props.items=[]] - Array of { label, onClick, href, current }
 * @param {string} [props.separator="/"] - Separator between items
 * @param {string} [props.className] - Additional CSS classes
 * @param {boolean} [props.hideMobileBackBar=false] - Opt out of the mobile
 *   back bar entirely (e.g. Browse, which is a primary bottom-nav
 *   destination on mobile, not a page you'd navigate back out of).
 */
export function Breadcrumbs({ items = [], separator = '/', className = '', hideMobileBackBar = false }) {
  const location = useLocation();
  const currentItem = items[items.length - 1];
  const parentItem = items.length > 1 ? items[items.length - 2] : null;
  const goBack = useSmartBack(parentItem?.href);

  if (!items.length) {
    return null;
  }

  // BreadcrumbList schema. The trailing "current page" item conventionally
  // has no href (it's not a link to itself) - but that's the most common
  // shape (Home > Current Page), so without inferring its URL from the
  // current route, a 2-level trail could never produce a valid 2-item
  // schema. Any other item without an href (onClick-only, no known URL) is
  // just omitted, since a wrong URL in structured data is worse than a
  // shorter trail.
  const breadcrumbListJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items
      .map((item, index) => {
        const isLast = index === items.length - 1 || item.current;
        const url = item.href
          ? `${SITE_URL}${item.href}`
          : isLast
            ? `${SITE_URL}${location.pathname}`
            : null;
        return url ? { '@type': 'ListItem', position: index + 1, name: item.label, item: url } : null;
      })
      .filter(Boolean),
  };

  return (
    <>
      <nav
        className={['ui-breadcrumbs', className].filter(Boolean).join(' ')}
        aria-label="Breadcrumb"
      >
        {breadcrumbListJsonLd.itemListElement.length > 1 && (
          <Helmet>
            <script type="application/ld+json">{JSON.stringify(breadcrumbListJsonLd)}</script>
          </Helmet>
        )}
        <ol className="ui-breadcrumbs__list">
          {items.map((item, index) => {
            const isLast = index === items.length - 1 || item.current;

            return (
              <li key={`${item.label}-${index}`} className="ui-breadcrumbs__item">
                {item.href && !isLast ? (
                  <Link className="ui-breadcrumbs__link" to={item.href} onClick={item.onClick}>
                    {item.label}
                  </Link>
                ) : item.onClick && !isLast ? (
                  <button type="button" className="ui-breadcrumbs__link" onClick={item.onClick}>
                    {item.label}
                  </button>
                ) : (
                  <span
                    className="ui-breadcrumbs__current"
                    aria-current={isLast ? 'page' : undefined}
                  >
                    {item.label}
                  </span>
                )}

                {!isLast && (
                  <span className="ui-breadcrumbs__separator" aria-hidden="true">
                    {separator}
                  </span>
                )}
              </li>
            );
          })}
        </ol>
      </nav>

      {!hideMobileBackBar && (
        <div className="ui-mobile-backbar">
          <button
            type="button"
            className="ui-mobile-backbar__back"
            onClick={goBack}
            aria-label="Go back"
          >
            <ChevronLeftIcon size={18} />
          </button>
          <span className="ui-mobile-backbar__title">{currentItem?.label}</span>
        </div>
      )}
    </>
  );
}

/**
 * Tabs - Tabbed content sections with accessible tablist behavior
 * @param {Object} props
 * @param {Array} [props.items=[]] - Array of { id, label, content }
 * @param {string} [props.defaultTabId] - Initially active tab id
 * @param {string} [props.className] - Additional CSS classes
 */
export function Tabs({ items = [], defaultTabId, className = '' }) {
  const fallbackId = items[0]?.id;
  const [activeTabId, setActiveTabId] = React.useState(defaultTabId || fallbackId);
  const tabSetId = React.useId();

  React.useEffect(() => {
    if (!items.some((item) => item.id === activeTabId)) {
      setActiveTabId(defaultTabId || items[0]?.id);
    }
  }, [activeTabId, defaultTabId, items]);

  if (!items.length) {
    return null;
  }

  const activeTab = items.find((item) => item.id === activeTabId) || items[0];

  return (
    <div className={['ui-tabs', className].filter(Boolean).join(' ')}>
      <div className="ui-tabs__list" role="tablist" aria-label="Content sections">
        {items.map((item) => {
          const isActive = item.id === activeTab.id;
          return (
            <button
              key={item.id}
              type="button"
              id={`${tabSetId}-tab-${item.id}`}
              className={['ui-tabs__tab', isActive && 'ui-tabs__tab--active']
                .filter(Boolean)
                .join(' ')}
              role="tab"
              aria-selected={isActive}
              aria-controls={`${tabSetId}-panel-${item.id}`}
              tabIndex={isActive ? 0 : -1}
              onClick={() => setActiveTabId(item.id)}
            >
              {item.label}
            </button>
          );
        })}
      </div>

      <div
        id={`${tabSetId}-panel-${activeTab.id}`}
        className="ui-tabs__panel"
        role="tabpanel"
        aria-labelledby={`${tabSetId}-tab-${activeTab.id}`}
      >
        {activeTab.content}
      </div>
    </div>
  );
}

// ============================================================================
// ORGANISMS - Complex Sections
// ============================================================================

/**
 * Drawer - Slide-out panel for filters, alerts, or navigation
 * @param {Object} props
 * @param {boolean} props.isOpen - Is drawer open
 * @param {function} props.onClose - Close handler
 * @param {React.ReactNode} props.children - Drawer content
 * @param {React.ReactNode} [props.header] - Optional header
 * @param {React.ReactNode} [props.footer] - Optional footer
 * @param {string} [props.variant="default"] - Drawer variant
 * @param {string} [props.className] - Additional CSS classes
 */
export function Drawer({
  isOpen,
  onClose,
  children,
  header,
  footer,
  variant = 'default',
  className = '',
  ...rest
}) {
  const baseClass = 'ui-drawer';
  const variantClass = variant !== 'default' ? `${baseClass}--${variant}` : '';
  const finalClass = [baseClass, variantClass, isOpen && `${baseClass}--open`, className]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      {isOpen && (
        <button type="button" className="ui-drawer-overlay" onClick={onClose} aria-label="Close" />
      )}
      <aside className={finalClass} {...rest}>
        {header && <div className="ui-drawer__header">{header}</div>}
        <div className="ui-drawer__body">{children}</div>
        {footer && <div className="ui-drawer__footer">{footer}</div>}
      </aside>
    </>
  );
}

/**
 * PropertyCard - Complete listing card with image, price, and metadata
 * @param {Object} props
 * @param {Object} props.property - Property data object
 * @param {string} props.property.image - Image URL
 * @param {string} props.property.title - Property title
 * @param {number} props.property.price - Property price
 * @param {function} [props.onSelect] - Selection handler
 * @param {React.ReactNode} [props.children] - Custom card content
 * @param {string} [props.className] - Additional CSS classes
 */
export function PropertyCard({ property, onSelect, className = '', children, ...rest }) {
  const baseClass = 'ui-property-card';
  const finalClass = [baseClass, className].filter(Boolean).join(' ');

  return (
    <article className={finalClass} {...rest}>
      {children || (
        <>
          <img src={property.image} alt={property.title} />
          <div className="ui-property-card__body">
            <h3>{property.title}</h3>
            <p className="ui-property-card__price">
              ₦{Number(property.price || 0).toLocaleString()}
            </p>
            {onSelect && <button onClick={() => onSelect(property)}>View details</button>}
          </div>
        </>
      )}
    </article>
  );
}

/**
 * Grid - Responsive grid container for layouts
 * @param {Object} props
 * @param {React.ReactNode} props.children - Grid items
 * @param {string} [props.variant="auto-fit"] - "auto-fit" (responsive) or number for fixed columns
 * @param {string} [props.minItemWidth="220px"] - Minimum item width for auto-fit
 * @param {string} [props.gap="14px"] - Gap between items
 * @param {string} [props.className] - Additional CSS classes
 */
export function Grid({
  children,
  variant = 'auto-fit',
  minItemWidth = '220px',
  gap = '14px',
  className = '',
  ...rest
}) {
  const isNumeric = /^\d+$/.test(variant);
  const columns = isNumeric ? `${variant}` : variant;
  const gridTemplate = isNumeric
    ? `repeat(${columns}, minmax(0, 1fr))`
    : `repeat(${columns}, minmax(${minItemWidth}, 1fr))`;

  const style = {
    display: 'grid',
    gridTemplateColumns: gridTemplate,
    gap,
    ...rest.style,
  };

  const finalClass = ['ui-grid', className].filter(Boolean).join(' ');

  return (
    <div className={finalClass} style={style} {...rest}>
      {children}
    </div>
  );
}

/**
 * Section - Content section with optional header and decorative kicker
 * @param {Object} props
 * @param {React.ReactNode} props.children - Section content
 * @param {string} [props.title] - Section title
 * @param {string} [props.subtitle] - Section subtitle/description
 * @param {React.ReactNode} [props.kicker] - Small label above title (icon + text)
 * @param {string} [props.className] - Additional CSS classes
 */
export function Section({ children, title, subtitle, kicker, className = '', ...rest }) {
  const finalClass = ['ui-section', className].filter(Boolean).join(' ');

  return (
    <section className={finalClass} {...rest}>
      {(title || kicker) && (
        <div className="ui-section__header">
          {kicker && <p className="ui-section__kicker">{kicker}</p>}
          {title && <h2 className="ui-section__title">{title}</h2>}
          {subtitle && <p className="ui-section__subtitle">{subtitle}</p>}
        </div>
      )}
      <div className="ui-section__body">{children}</div>
    </section>
  );
}

// ============================================================================
// COMPONENT LIBRARY EXPORT SUMMARY
// ============================================================================

/**
 * Component Library - Complete source of truth for all UI components
 *
 * ATOMS (5):
 * - Button: Primary/secondary/text variants, icon support, disabled state
 * - Input: Text, email, number inputs with optional icon prefix
 * - Select: Dropdown with custom options
 * - Badge: Status indicators (info, success, warning, error)
 * - Tag: Chip-style labels (normal or filled)
 *
 * MOLECULES (5):
 * - FormField: Label + input wrapper with error handling
 * - Card: Container with optional header/footer
 * - MetaLine: Icon + text metadata line
 * - RadioCard: Card-style radio button with label/description
 * - Alert: Status message with ARIA support
 *
 * ORGANISMS (4):
 * - Drawer: Slide-out panel with overlay
 * - PropertyCard: Listing card template
 * - Grid: Responsive grid layout (auto-fit or fixed columns)
 * - Section: Content section with kicker, title, subtitle
 *
 * All components support:
 * - className prop for custom styling
 * - ...rest for native HTML attributes
 * - Icon integration via icon prop
 * - Accessibility features (ARIA, semantic HTML)
 * - Responsive design
 */

// ============================================================================
// ORGANISMS - Complex, self-contained UI sections
// ============================================================================

/**
 * ImageCarousel - Swipeable multi-image carousel for property listings
 *
 * Features:
 * - Single image shown when only one provided (no controls rendered)
 * - Prev / Next arrow controls + dot pager + image counter when multiple images
 * - Touch / pointer swipe support (40 px threshold, left = next, right = prev)
 * - Arrow key navigation (← →)
 * - Drag suppression keeps parent card click intact
 * - Graceful fallback image on load error
 *
 * CSS classes (all scoped under `.property-image-carousel`):
 * - `.property-image-carousel__image`  — the visible img element
 * - `.property-image-carousel__control` — prev / next arrow buttons
 * - `.property-image-carousel__dots`   — dot pager container
 * - `.property-image-carousel__dot`    — individual dot; `.active` for current
 * - `.property-image-carousel__counter` — "1/4" counter overlay
 *
 * @param {Object}   props
 * @param {string[]} [props.images=[]]            - Ordered array of image URLs
 * @param {string}   [props.fallbackImage=""]     - Shown when images is empty or on error
 * @param {string}   [props.alt="Property image"] - Alt text for the active image
 * @param {string}   [props.className=""]         - Extra classes on the root element
 *
 * @example
 * import { ImageCarousel } from './components';
 *
 * <ImageCarousel
 *   images={property.images}
 *   fallbackImage={placeholderImage}
 *   alt={property.title}
 * />
 */
export function ImageCarousel({
  images = [],
  fallbackImage = '',
  alt = 'Property image',
  className = '',
}) {
  const imageSet = React.useMemo(() => {
    const deduped = [...new Set((Array.isArray(images) ? images : []).filter(Boolean))];
    if (deduped.length) {
      return deduped;
    }
    return fallbackImage ? [fallbackImage] : [];
  }, [images, fallbackImage]);

  const [activeIndex, setActiveIndex] = React.useState(0);
  const dragStartX = React.useRef(null);
  const isDragging = React.useRef(false);

  React.useEffect(() => {
    setActiveIndex(0);
  }, [imageSet.length]);

  if (!imageSet.length) {
    return <div className={`property-image-carousel ${className}`.trim()} />;
  }

  const hasMultiple = imageSet.length > 1;

  const goPrev = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex((i) => (i === 0 ? imageSet.length - 1 : i - 1));
  };

  const goNext = (event) => {
    event.preventDefault();
    event.stopPropagation();
    setActiveIndex((i) => (i === imageSet.length - 1 ? 0 : i + 1));
  };

  const handlePointerDown = (event) => {
    dragStartX.current = event.clientX;
    isDragging.current = false;
  };

  const handlePointerMove = (event) => {
    if (dragStartX.current === null) {
      return;
    }
    if (Math.abs(event.clientX - dragStartX.current) > 6) {
      isDragging.current = true;
    }
  };

  const handlePointerUp = (event) => {
    if (dragStartX.current === null || !hasMultiple) {
      dragStartX.current = null;
      return;
    }
    const delta = event.clientX - dragStartX.current;
    dragStartX.current = null;
    if (Math.abs(delta) < 40) {
      return;
    }
    if (delta < 0) {
      setActiveIndex((i) => (i === imageSet.length - 1 ? 0 : i + 1));
    } else {
      setActiveIndex((i) => (i === 0 ? imageSet.length - 1 : i - 1));
    }
  };

  const handleCardClick = (event) => {
    if (isDragging.current) {
      event.preventDefault();
      event.stopPropagation();
      isDragging.current = false;
    }
  };

  const handleKeyDown = (event) => {
    if (!hasMultiple) {
      return;
    }
    if (event.key === 'ArrowLeft') {
      event.stopPropagation();
      setActiveIndex((i) => (i === 0 ? imageSet.length - 1 : i - 1));
    } else if (event.key === 'ArrowRight') {
      event.stopPropagation();
      setActiveIndex((i) => (i === imageSet.length - 1 ? 0 : i + 1));
    }
  };

  return (
    <div
      className={`property-image-carousel ${className}`.trim()}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onClick={handleCardClick}
      onKeyDown={handleKeyDown}
      role="region"
      aria-label="Property images"
    >
      <img
        src={imageSet[activeIndex]}
        alt={alt}
        className="property-image-carousel__image"
        onError={(event) => {
          event.currentTarget.onerror = null;
          if (fallbackImage) {
            event.currentTarget.src = fallbackImage;
          }
        }}
      />

      {hasMultiple && (
        <>
          <button
            type="button"
            className="property-image-carousel__control property-image-carousel__control--prev"
            aria-label="Previous image"
            onClick={goPrev}
          >
            ‹
          </button>
          <button
            type="button"
            className="property-image-carousel__control property-image-carousel__control--next"
            aria-label="Next image"
            onClick={goNext}
          >
            ›
          </button>
          <div className="property-image-carousel__dots" aria-label="Image pager">
            {imageSet.map((image, index) => (
              <button
                key={`${image}-${index}`}
                type="button"
                className={`property-image-carousel__dot ${
                  index === activeIndex ? 'active' : ''
                }`.trim()}
                aria-label={`Show image ${index + 1}`}
                aria-current={index === activeIndex ? 'true' : undefined}
                onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  setActiveIndex(index);
                }}
              />
            ))}
          </div>
          <span className="property-image-carousel__counter">
            {activeIndex + 1}/{imageSet.length}
          </span>
        </>
      )}
    </div>
  );
}

// ============================================================================
// MODAL COMPONENT
// ============================================================================

/**
 * Modal - Centered overlay dialog (organism)
 *
 * @param {Object}   props
 * @param {boolean}  props.isOpen            - Controls visibility
 * @param {Function} props.onClose           - Called when backdrop or × is clicked
 * @param {string}   [props.title]           - Dialog title rendered in the header
 * @param {string}   [props.size="md"]       - "sm" | "md" | "lg" | "full"
 * @param {React.ReactNode} props.children   - Dialog body content
 * @param {React.ReactNode} [props.footer]   - Optional footer slot (action buttons)
 * @param {string}   [props.className]       - Extra classes on the dialog box
 * @param {string}   [props.mobilePresentation] - "sheet" opts into a bottom-sheet
 *   layout below 760px width (slide-up, full-width, rounded top corners only).
 *   Omit for the default centered-dialog behavior on every viewport (unchanged
 *   for all existing callers).
 *
 * @example
 * <Modal isOpen={show} onClose={() => setShow(false)} title="Confirm action">
 *   <p>Are you sure you want to continue?</p>
 * </Modal>
 */
export function Modal({
  isOpen,
  onClose,
  title,
  ariaLabel,
  size = 'md',
  children,
  footer,
  className = '',
  mobilePresentation,
}) {
  React.useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) {
    return null;
  }

  const presentationClass = mobilePresentation === 'sheet' ? 'ui-modal--mobile-sheet' : '';

  return (
    <>
      {/* Backdrop */}
      <button
        type="button"
        className="ui-modal-backdrop"
        aria-label="Close dialog"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={ariaLabel || (typeof title === 'string' ? title : 'Dialog')}
        className={`ui-modal ui-modal--${size} ${presentationClass} ${className}`.trim()}
      >
        {/* Header */}
        <div className="ui-modal__header">
          {title && <h3 className="ui-modal__title">{title}</h3>}
          <button
            type="button"
            className="ui-modal__close"
            aria-label="Close dialog"
            onClick={onClose}
          >
            ✕
          </button>
        </div>

        {/* Body */}
        <div className="ui-modal__body">{children}</div>

        {/* Footer */}
        {footer && <div className="ui-modal__footer">{footer}</div>}
      </div>
    </>
  );
}

// ============================================================================
// LOADING SPINNER COMPONENT
// ============================================================================

/**
 * LoadingSpinner - Animated circular loading indicator (atom)
 *
 * @param {Object} props
 * @param {string} [props.size="md"]   - "sm" | "md" | "lg"
 * @param {string} [props.label="Loading..."] - Accessible label (visible below spinner)
 * @param {boolean} [props.inline=false] - When true, renders inline without centering wrapper
 * @param {string} [props.className]   - Extra classes on the wrapper
 *
 * @example
 * <LoadingSpinner size="lg" label="Loading properties..." />
 * <LoadingSpinner size="sm" inline />
 */
export function LoadingSpinner({
  size = 'md',
  label = 'Loading...',
  inline = false,
  className = '',
}) {
  return (
    <div
      className={`ui-spinner-wrap ${inline ? 'ui-spinner-wrap--inline' : ''} ${className}`.trim()}
      role="status"
      aria-label={label}
    >
      <div className={`ui-spinner ui-spinner--${size}`} aria-hidden="true" />
      {label && !inline && (
        <p className="ui-spinner__label" aria-hidden="true">
          {label}
        </p>
      )}
    </div>
  );
}

// ============================================================================
// PAGINATION COMPONENT
// ============================================================================

/**
 * Pagination - Page navigation controls (molecule)
 *
 * @param {Object}   props
 * @param {number}   props.currentPage    - 1-based current page number
 * @param {number}   props.totalPages     - Total number of pages
 * @param {Function} props.onPageChange   - Called with new 1-based page number
 * @param {number}   [props.siblingCount=1] - Pages to show on each side of current
 * @param {string}   [props.className]    - Extra classes on the nav element
 *
 * @example
 * <Pagination currentPage={page} totalPages={12} onPageChange={setPage} />
 */
export function Pagination({
  currentPage,
  totalPages,
  onPageChange,
  siblingCount = 1,
  className = '',
}) {
  if (totalPages <= 1) {
    return null;
  }

  // Build page number array with ellipsis markers
  const buildPages = () => {
    const pages = [];
    const leftBound = Math.max(1, currentPage - siblingCount);
    const rightBound = Math.min(totalPages, currentPage + siblingCount);

    // Always show page 1
    if (leftBound > 2) {
      pages.push(1, '...');
    } else if (leftBound === 2) {
      pages.push(1);
    }

    for (let i = leftBound; i <= rightBound; i++) {
      pages.push(i);
    }

    // Always show last page
    if (rightBound < totalPages - 1) {
      pages.push('...', totalPages);
    } else if (rightBound === totalPages - 1) {
      pages.push(totalPages);
    }

    return pages;
  };

  const pages = buildPages();

  return (
    <nav className={`ui-pagination ${className}`.trim()} aria-label="Page navigation">
      <button
        type="button"
        className="ui-pagination__btn ui-pagination__btn--prev"
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        aria-label="Previous page"
      >
        ‹ Prev
      </button>

      <ol className="ui-pagination__pages">
        {pages.map((page, index) =>
          page === '...' ? (
            <li key={`ellipsis-${index}`} className="ui-pagination__ellipsis" aria-hidden="true">
              …
            </li>
          ) : (
            <li key={page}>
              <button
                type="button"
                className={`ui-pagination__btn ${
                  page === currentPage ? 'ui-pagination__btn--active' : ''
                }`}
                onClick={() => onPageChange(page)}
                aria-label={`Page ${page}`}
                aria-current={page === currentPage ? 'page' : undefined}
              >
                {page}
              </button>
            </li>
          ),
        )}
      </ol>

      <button
        type="button"
        className="ui-pagination__btn ui-pagination__btn--next"
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        aria-label="Next page"
      >
        Next ›
      </button>
    </nav>
  );
}

// ============================================================================
// ACCORDION COMPONENT
// ============================================================================

/**
 * Accordion - Collapsible content panels (organism)
 *
 * @param {Object}   props
 * @param {Array}    props.items           - Array of { id, title, content } objects
 * @param {boolean}  [props.allowMultiple=false] - Allow multiple panels open simultaneously
 * @param {string|number} [props.defaultOpenId]  - id of panel open by default
 * @param {string}   [props.className]    - Extra classes on the root element
 *
 * @example
 * <Accordion
 *   items={[
 *     { id: "overview", title: "Property overview", content: <ul>...</ul> },
 *     { id: "tenancy",  title: "Tenancy information", content: <div>...</div> },
 *   ]}
 * />
 */
export function Accordion({
  items = [],
  allowMultiple = false,
  defaultOpenId = null,
  className = '',
}) {
  const [openIds, setOpenIds] = React.useState(
    defaultOpenId !== null ? new Set([defaultOpenId]) : new Set(),
  );

  const toggle = (id) => {
    setOpenIds((prev) => {
      const next = new Set(allowMultiple ? prev : []);
      if (prev.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  return (
    <div className={`ui-accordion ${className}`.trim()}>
      {items.map((item) => {
        const isOpen = openIds.has(item.id);
        return (
          <div
            key={item.id}
            className={`ui-accordion__item ${isOpen ? 'ui-accordion__item--open' : ''}`.trim()}
          >
            <button
              type="button"
              id={`accordion-btn-${item.id}`}
              className="ui-accordion__trigger"
              aria-expanded={isOpen}
              aria-controls={`accordion-panel-${item.id}`}
              onClick={() => toggle(item.id)}
            >
              <span className="ui-accordion__trigger-label">{item.title}</span>
              <span className="ui-accordion__chevron" aria-hidden="true">
                {isOpen ? '▲' : '▼'}
              </span>
            </button>
            <div
              id={`accordion-panel-${item.id}`}
              role="region"
              aria-labelledby={`accordion-btn-${item.id}`}
              className="ui-accordion__panel"
              hidden={!isOpen}
            >
              <div className="ui-accordion__panel-inner">{item.content}</div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ============================================================================
// LOGO COMPONENT
// ============================================================================

/**
 * Logo - React atom icon (default CRA logo)
 * @param {Object} props
 * @param {string} [props.size="48"] - Logo size in pixels
 * @param {string} [props.className] - Additional CSS classes
 */
export function Logo({ size = '48', className = '', ...rest }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 841.9 595.3"
      width={size}
      height={size}
      className={`logo ${className}`}
      {...rest}
    >
      <g fill="#61DAFB">
        <path d="M666.3 296.5c0-32.5-40.7-63.3-103.1-82.4 14.4-63.6 8-114.2-20.2-130.4-6.5-3.8-14.1-5.6-22.4-5.6v22.3c4.6 0 8.3.9 11.4 2.6 13.6 7.8 19.5 37.5 14.9 75.7-1.1 9.4-2.9 19.3-5.1 29.4-19.6-4.8-41-8.5-63.5-10.9-13.5-18.5-27.5-35.3-41.6-50 32.6-30.3 63.2-46.9 84-46.9V78c-27.5 0-63.5 19.6-99.9 53.6-36.4-33.8-72.4-53.2-99.9-53.2v22.3c20.7 0 51.4 16.5 84 46.6-14 14.7-28 31.4-41.3 49.9-22.6 2.4-44 6.1-63.6 11-2.3-10-4-19.7-5.2-29-4.7-38.2 1.1-67.9 14.6-75.8 3-1.8 6.9-2.6 11.5-2.6V78.5c-8.4 0-16 1.8-22.6 5.6-28.1 16.2-34.4 66.7-19.9 130.1-62.2 19.2-102.7 49.9-102.7 82.3 0 32.5 40.7 63.3 103.1 82.4-14.4 63.6-8 114.2 20.2 130.4 6.5 3.8 14.1 5.6 22.5 5.6 27.5 0 63.5-19.6 99.9-53.6 36.4 33.8 72.4 53.2 99.9 53.2 8.4 0 16-1.8 22.6-5.6 28.1-16.2 34.4-66.7 19.9-130.1 62-19.1 102.5-49.9 102.5-82.3zm-130.2-66.7c-3.7 12.9-8.3 26.2-13.5 39.5-4.1-8-8.4-16-13.1-24-4.6-8-9.5-15.8-14.4-23.4 14.2 2.1 27.9 4.7 41 7.9zm-45.8 106.5c-7.8 13.5-15.8 26.3-24.1 38.2-14.9 1.3-30 2-45.2 2-15.1 0-30.2-.7-45-1.9-8.3-11.9-16.4-24.6-24.2-38-7.6-13.1-14.5-26.4-20.8-39.8 6.2-13.4 13.2-26.8 20.7-39.9 7.8-13.5 15.8-26.3 24.1-38.2 14.9-1.3 30-2 45.2-2 15.1 0 30.2.7 45 1.9 8.3 11.9 16.4 24.6 24.2 38 7.6 13.1 14.5 26.4 20.8 39.8-6.3 13.4-13.2 26.8-20.7 39.9zm32.3-13c5.4 13.4 10 26.8 13.8 39.8-13.1 3.2-26.9 5.9-41.2 8 4.9-7.7 9.8-15.6 14.4-23.7 4.6-8 8.9-16.1 13-24.1zM421.2 430c-9.3-9.6-18.6-20.3-27.8-32 9 .4 18.2.7 27.5.7 9.4 0 18.7-.2 27.8-.7-9 11.7-18.3 22.4-27.5 32zm-74.4-58.9c-14.2-2.1-27.9-4.7-41-7.9 3.7-12.9 8.3-26.2 13.5-39.5 4.1 8 8.4 16 13.1 24 4.7 8 9.5 15.8 14.4 23.4zM420.7 163c9.3 9.6 18.6 20.3 27.8 32-9-.4-18.2-.7-27.5-.7-9.4 0-18.7.2-27.8.7 9-11.7 18.3-22.4 27.5-32zm-74 58.9c-4.9 7.7-9.8 15.6-14.4 23.7-4.6 8-8.9 16-13 24-5.4-13.4-10-26.8-13.8-39.8 13.1-3.1 26.9-5.8 41.2-7.9zm-90.5 125.2c-35.4-15.1-58.3-34.9-58.3-50.6 0-15.7 22.9-35.6 58.3-50.6 8.6-3.7 18-7 27.7-10.1 5.7 19.6 13.2 40 22.5 60.9-9.2 20.8-16.6 41.1-22.2 60.6-9.9-3.1-19.3-6.5-28-10.2zM310 490c-13.6-7.8-19.5-37.5-14.9-75.7 1.1-9.4 2.9-19.3 5.1-29.4 19.6 4.8 41 8.5 63.5 10.9 13.5 18.5 27.5 35.3 41.6 50-32.6 30.3-63.2 46.9-84 46.9-4.5-.1-8.3-1-11.3-2.7zm237.2-76.2c4.7 38.2-1.1 67.9-14.6 75.8-3 1.8-6.9 2.6-11.5 2.6-20.7 0-51.4-16.5-84-46.6 14-14.7 28-31.4 41.3-49.9 22.6-2.4 44-6.1 63.6-11 2.3 10.1 4.1 19.8 5.2 29.1zm38.5-66.7c-8.6 3.7-18 7-27.7 10.1-5.7-19.6-13.2-40-22.5-60.9 9.2-20.8 16.6-41.1 22.2-60.6 9.9 3.1 19.3 6.5 28.1 10.2 35.4 15.1 58.3 34.9 58.3 50.6-.1 15.7-23 35.6-58.4 50.6zM320.8 78.4z" />
        <circle cx="420.9" cy="296.5" r="45.7" />
        <path d="M520.5 78.1z" />
      </g>
    </svg>
  );
}

const componentLibrary = {
  // Atoms
  Button,
  Input,
  Select,
  Badge,
  Tag,
  Tooltip,
  LoadingSpinner,
  Logo,
  // Molecules
  FormField,
  Card,
  MetaLine,
  RadioCard,
  Alert,
  Breadcrumbs,
  Pagination,
  Tabs,
  // Organisms
  Drawer,
  PropertyCard,
  Grid,
  Section,
  ImageCarousel,
  Modal,
  Accordion,
};

export default componentLibrary;
